/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 */

import { css, html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators";

import "@maicol07/material-web-additions/data-table/data-table";
import "@maicol07/material-web-additions/data-table/data-table-row";
import "@maicol07/material-web-additions/data-table/data-table-column";
import "@maicol07/material-web-additions/data-table/data-table-cell";

import "@material/mwc-list/mwc-list";
import "@material/mwc-list/mwc-check-list-item";
import "@material/mwc-button";
import "@material/mwc-textfield";
import "@material/mwc-linear-progress";
import "@material/mwc-select";

import "@material/web/textfield/outlined-text-field";
import "@material/web/iconbutton/icon-button";

import { Client } from "../data-classes/client";
import { Consultant } from "../data-classes/consultant";
import { HitlElement } from "../hitl-element";
import { ConsultantClient } from "../data-classes/consultant-client";

/**
 * Component to manage clients. It used by hitl-administration
 */
@customElement("hitl-clients")
export class HitlClients extends HitlElement {
  /**
   * Selected clients
   */
  @property({ type: Array })
  selectedClients: Client[] = [];
  /**
   * Boolean per default its false. When a new client should be created this value becomes true and makes the a new row visible, if the value persists as true. 
   * 
   */
  @property({ type: Boolean })
  addClient: boolean = false;
  /**
   * Variable for new client
   */
  @property({ type: Object })
  newClient: Client = new Client(BigInt(0), "");
  /**
   * Dialog text
   */
  dialogText = document.createElement('p');

  /**
   * Ok Button for dialog
   */
  footerButtonOk = document.createElement('mwc-button');
  
  /**
   * Temp selected rows
   */
  @property({type: Array})
  tempSelectedRows =[];
  
  /**
   * Boolean to check if event listener was set. 
   * Default: false
   */
  @property({type: Boolean})
  eventListenersAdded = false;
  
  /**
   * Timer id for connected- and callback
   */
  private timerId: any;

  static override styles = css`
    .mdc-card > div {
      margin: 0px 12px 0 12px;
    }

    .mdc-card__actions {
      gap: 12px;
    }

    md-data-table-footer {
      gap: 10px;
      align-items: center;
    }

    md-outlined-text-field {
      padding: 10px 0 10px;
      width: 100%;
    }

    md-data-table-cell {
        background-color: #ffffff;
    }

    md-data-table-column {
        background-color: #ffffff;
    }

    md-data-table-footer {
        background-color: #ffffff;
    }

    mwc-select {
        margin: 8px 0px;
        width: -webkit-fill-available;
    }
  `;
  
  protected override async firstUpdated(){
      const accessToken = await this.getAccessToken();
      //Initialization clients and consultantClients
      this.clients = await Client.list(this.database!, accessToken);
      this.consultantClients = await ConsultantClient.list(this.database!, accessToken);
    
      //Set event for the mwc-selector for each row
      if(!this.eventListenersAdded){
        const selects = this.renderRoot?.querySelectorAll("mwc-select.consultant-select");
        if(selects){
           selects.forEach( (item) =>{        
                item.addEventListener("selected", this.addConsultantToClient);
           });
        this.eventListenersAdded = true; 
        }
      }
  }

  override async connectedCallback() {
    super.connectedCallback();
    //Interval in which consultants are updated each 5 sec. = 5000 msec.
    this.timerId = setInterval(async () => {
      this.updateConsultants();
      console.log("Connected Callback clients")
    }, 5000);
  }

  override async disconnectedCallback() {
    super.disconnectedCallback();
    //Interval for consultants get disconnected after leaving the page with this component
    if(this.timerId)clearInterval(this.timerId)
  }

  override render() {
    console.log("rendering hitl-clients");
    const template: TemplateResult[] = [];
    template.push(html`
      <div class="mdc-card">
        <div class="card-header">
          <div class="card-title">
            <h2 class="mdc-typography--headline6">Mandanten Administration</h2>
          </div>
        </div>
        <div class="card-body mdc-typography--body2">
          <md-data-table
            aria-label="Mandanten Administration"
            density=""
            page-sizes-label="Zeilen pro Seite:"
            first-row-of-page=""
            current-page-size=""
            last-row-of-page=""
            paginated="true"
            pagination-total-label=":firstRow-:lastRow of :totalRows"
            @row-selection-changed=${this.rowSelectionChanged}
            @selected-all=${this.rowSelectionChanged}
            @unselected-all=${this.rowSelectionChanged}>
            <md-data-table-column type="checkbox"></md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              Mandantennummer
            </md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              Name
            </md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              Zugeordneter Berater
            </md-data-table-column>
            ${this.addClient
              ? html`<md-data-table-row data-id="-1">
                  <md-data-table-cell>
  <mwc-icon-button @click=${() =>{
    (this.addClient =
      false); 
      this.newClient = 
        new Client(BigInt(0), "");}} icon="cancel"></mwc-icon-button></md-data-table-cell>                
                  <md-data-table-cell>
                    <md-outlined-text-field @input=${this.inputClientID} type="number" required id="clientID" label="Mandantennummer" maxlength="5" value=${
                      this.newClient.clientID
                    }
                    @keydown="${this.handleKeySaveNewClient}"
                    >
                    </md-outlined-text-field>
                  </md-data-table-cell>
                  <md-data-table-cell>
                    <md-outlined-text-field @input=${this.inputName} required id="name" label="Name" value=${
                      this.newClient.name
                    }
                    @keydown="${this.handleKeySaveNewClient}"
                    >
                 </md-outlined-text-field>
                  </md-data-table-cell>                  
                  <md-data-table-cell>
                </md-data-table-cell> 
                </md-data-table-row>`
              : null}
            ${this.clients.map((client: Client) => {
              return html`
                <md-data-table-row data-id=${client.clientID}>
                  <md-data-table-cell type="checkbox"></md-data-table-cell>
                  <md-data-table-cell type="numeric">${client.clientID}</md-data-table-cell>
                  <md-data-table-cell>
                    <mwc-textfield
                    outlined
                    id='name-${client.clientID}'
                    TextFieldType="text"
                    value= ${client.name}
                    @keydown="${this.handleKeyUpdateClientName}"
                    ?disabled=${this.selectedClients.length === 0 || this.selectedClients.length > 1 || !this.selectedClients.includes(client)}
                    >
                    ${client.name}
                    </mwc-textfield>
                  </md-data-table-cell>
                  <md-data-table-cell>
                    <mwc-select
                    outlined
                      label="Berater"
                      fixedMenuPosition="true"
                      class="consultant-select"
                      ?disabled=${this.selectedClients.length === 0 || this.selectedClients.length > 1 || !this.selectedClients.includes(client)}
                      >
                      ${this.consultants.map((consultant: Consultant) => {
                        
                        return html`
                        ${this.consultantClients.find((consultantClient: ConsultantClient) => BigInt(consultantClient.consultantID) == consultant.consultantID && BigInt(consultantClient.clientID) == client.clientID)
                            ?
                            html`
                                <mwc-list-item selected value="${consultant.consultantID}">
                                    ${consultant.consultantID} - ${consultant.name}
                                </mwc-list-item>`
                            :
                            html`
                                <mwc-list-item value="${consultant.consultantID}">
                                    ${consultant.consultantID} - ${consultant.name}
                                </mwc-list-item>`
                            
                        }
                        `;
                      })}
                        <mwc-list-item ?selected=${this.consultantClients.find((consultantClient: ConsultantClient) => BigInt(consultantClient.clientID) == client.clientID) == undefined} value='0n'>
                            Berater
                        </mwc-list-item>
                    </mwc-select>
                  </md-data-table-cell>
                </md-data-table-row>
              `;
            })}
            <md-data-table-footer
              slot="footer"
              style="display: flex; align-items: left; justify-content: left;">
              <mwc-button
                raised
                @click=${() => (this.addClient = true)}
                label="Neuen Mandanten hinzufügen"
                ?disabled=${this.addClient}></mwc-button>
              <mwc-button
                raised
                @click=${this.saveClient}
                label="Neuen Mandanten speichern"
                ?disabled=${!this.addClient}></mwc-button>
              <mwc-button
                raised
                @click=${this.deleteClients}
                label="Ausgewählte Mandanten löschen"
                ?disabled=${this.addClient ||
                this.selectedClients.length === 0}></mwc-button>
              <mwc-button
                raised
                @click=${this.updateClientName}
                label="Änderungen speichern"
                ?disabled=${this.addClient || this.selectedClients.length === 0}
                ></mwc-button>
              ${this.selectedClients.length > 0
                ? html`<mwc-button
                raised
                @click=${this.cancelEdit}
                label="Bearbeitung abbrechen"
                icon="cancel"
                ></mwc-button>`
            :null}
            </md-data-table-footer>
          </md-data-table>
        </div>
      </div>
    `);
    return template;
  }

  /**
   * Special event from the package maicol07/material-web-additions/data-table/data-table.
   * It triggers if the row selection in the data-table changes
   * @param e 
   */
  rowSelectionChanged = async (e: CustomEvent) => {
    console.log("rowSelectionChanged", e);
    this.tempSelectedRows = e.target!.rows!.filter((row: any) => row.selected);
    this.selectedClients = e
      .target!.rows!.filter((row: any) => row.selected)
      .map((row: any) =>
        this.clients.find((client) => client.clientID === BigInt(row.dataset.id))
      );
  };

  /**
   * Updates client name
   * @param _ 
   */
  updateClientName = async (_: Event) => {
    const accessToken = await this.getAccessToken(true);
    const clientName = (this.renderRoot.querySelector(`#name-${this.selectedClients[0].clientID}`) as HTMLInputElement)
    .value;
    //If client name exists then the name will be updated
    if(clientName){
      const newClient = new Client(this.selectedClients[0].clientID, clientName);
      newClient.updateClient(this.database!, accessToken).then(()=> console.log("Name updated"));
    this.clients.forEach(client =>{
        if(this.selectedClients[0].clientID == client.clientID) client.name = clientName;
    });
    this.tempSelectedRows.forEach((row:any) =>{
        row.selected = false;
      });
    this.selectedClients = [];
    this.requestUpdate();
    //If clientname is empty, then the user gets a pop up to put a value in the field
    } else {
        this.dialogText.innerHTML= "Geben Sie bitte den Namen des Mandanten ein";
        this.dialogInit();
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        
        this.dialog!.open = true;
        this.footerButtonOk.addEventListener('click', ()=> {
            this.dialog!.open=false;
        });
    }
  };

  /**
   * Cancel clientName edit
   * @param _ 
   */
  cancelEdit = (_:Event) => {   
    this.tempSelectedRows.forEach((row: any) => {
        let textfield = (this.shadowRoot?.getElementById(`name-${row.dataset.id}`) as HTMLInputElement);
        const client = this.clients.find((client: Client) => client.clientID == BigInt(row.dataset.id));
        if(textfield.value != client?.name){
            textfield.setAttribute('value', client!.name);
            return;   
        }
    })
    this.tempSelectedRows.forEach((row:any)=> {
        row.selected = false;
    });
    this.selectedClients = [];
    this.requestUpdate();
  }

  /**
   * Update consultants list
   */
  updateConsultants = async () => {
        const accessToken = await this.getAccessToken(true);
        if(this.database){
            this.consultants = await Consultant.list(this.database, accessToken);
            this.consultantClients = await ConsultantClient.list(this.database, accessToken);
            this.requestUpdate();
        }
  }

  /**
   * Update client list
   * @param _ 
   */
  updateClients = async (_:Event) => {
        const client = this.clients.find((client: Client) => this.newClient.clientID == client.clientID)
        console.log(client);
        const accessToken =  await this.getAccessToken(true);
        if(this.database)this.clients = await Client.list(this.database, accessToken);
        this.requestUpdate();
  }

  /**
   * Client id input for the new created client
   * @param e 
   */
  inputClientID = async (e: Event) => {
    const clientID = (e.target as HTMLInputElement).value;
    this.newClient.clientID = BigInt(clientID);
  }

  /**
   * Client name input for the new created client
   * @param e 
   */
  inputName = async (e: Event) => {
    const name = (e.target as HTMLInputElement).value;
    this.newClient.name = name;
  }

  /**
   * Handeling interaction with client name using keyboard keys
   * @param e 
   */
  handleKeyUpdateClientName(e: KeyboardEvent){
    if(e.key === "Enter"){                                          
        this.updateClientName(e as Event);
    }else  if(e.key === "Escape"){                                  
        this.cancelEdit(e as Event);
    }
  }

  
  /**
   * Handeling interaction with new client using keyboard keys
   * @param e 
   */
  handleKeySaveNewClient(e: KeyboardEvent){
    if(e.key === "Enter"){                                          
        this.saveClient(e as Event);
    }else  if(e.key === "Escape"){                                 
        this.addClient = false;
        this.newClient = new Client(BigInt(0), "");
    }
  }

  /**
   * Save new client
   * @param _ 
   */
  saveClient = async (_: Event) => {
    //Checks if the client does not exists
    if (this.database && 
        this.newClient.clientID.toString().length == 5 && 
        this.newClient.name != "") {
        
      if(!this.clients.find((client: Client) => client.clientID == this.newClient.clientID) && 
            !this.clients.find((client: Client) => client.name == this.newClient.name)){
        const accessToken = await this.getAccessToken(true);
        this.newClient.save(this.database, accessToken).then(async ()=>{
            this.consultants = await Consultant.list(this.database!, accessToken);
            this.consultantClients = await ConsultantClient.list(this.database!, accessToken);
            this.clients = await Client.list(this.database!, accessToken);
        });
        const client = new Client(BigInt(this.newClient.clientID), this.newClient.name);
        this.clients = this.clients.concat([client])
        this.addClient = false;
        this.dispatchEvent(
        new CustomEvent("clients-changed", {        //Access to clientsChange from hitl-app.ts
            detail: this.clients,
            bubbles: true,
            composed: true
        })
        );
        this.newClient = new Client(BigInt(0), "");
      } else if(this.clients.find((client: Client) => client.name == this.newClient.name)){ //Give a notification if the used clientName is used by another client
        this.dialogInit();
        this.dialogText.innerHTML ="Der angegebene Mandantennname existiert schon";
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.open = true;
      } else {                                                                              
        //Give a notification if the used clientID is used by another client
        this.dialogInit();
        this.dialogText.innerHTML ="Die angegebene Mandantennummer existiert schon";
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.open = true;
      } 
        
    //If the client exists then a pop up window gets shown
    } else{
        this.dialogInit();
        if(this.newClient.clientID.toString().length != 5 && this.newClient.name == ""){    //Validation clientid, clientname and if clientID size is not 5
            this.dialogText.innerHTML = "Geben Sie bitte eine Mandantennummer und einen Mandantenname ein!";
        }else if(this.newClient.name == ""){
            this.dialogText.innerHTML = "Geben Sie bitte einen Mandantenname ein!";
        } else {
            this.dialogText.innerHTML = "Geben Sie bitte eine Mandantennummer mit einer Zeichenlänge von 5 ein!";
        }
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.open = true;
    }
  };

  /**
   * Initialization of the dialog
   */
  dialogInit(){
        this.dialog!.innerHTML ="";
        this.footerButtonOk.setAttribute("slot", "primaryAction");
        this.footerButtonOk.setAttribute("dialogAction", "ok");
        this.footerButtonOk.innerText = "Ok";
        this.footerButtonOk.classList.add("raised");
        this.dialogText.style.color = "#000000";
        this.dialogText.style.padding = "0px 20%";
        this.dialog!.setAttribute('style', 'border-color: #000000; border-width: 1px; font-size: 1.1em;');
  }

  //This method is to delete clients
  /**
   * Deletes clients
   * @param _ 
   */
  deleteClients = async (_: CustomEvent) => {
      const accessToken = await this.getAccessToken(true);
      const selectedClientsIDs = this.selectedClients.map(
        (client: Client) => client.clientID
      );     
      //If a consultant-client connection is found, then the connection will deleted and then the client(s)
      if(this.consultantClients.find((item) => selectedClientsIDs.find((clientID) =>item.clientID == clientID))){
        const filteredClients = selectedClientsIDs.filter((clientID) => this.consultantClients.filter((consultantClient) => consultantClient.clientID == clientID ))
        ConsultantClient.deleteConsultantClients(this.database!, accessToken, filteredClients).then(()=>{
            Client.deleteClients(this.database!, accessToken, selectedClientsIDs);
        })
      //If consultant-client connection got not found then it deletes only the client(s)  
      } else {
        Client.deleteClients(this.database!, accessToken, selectedClientsIDs);
      }
      //Resets selection of the rows to unselected
      this.tempSelectedRows.forEach((row:any) =>{
        row.selected = false;
      });
      this.clients = this.clients.filter(
        (client: Client) => !this.selectedClients.includes(client)
      );
      //Empties the selectedClient list
      this.selectedClients=[];
      console.log(this.clients);
      this.requestUpdate();
  };

  /**
   * Add consultant to client
   * @param e 
   */
  addConsultantToClient = async (e: Event) => {  
    e.preventDefault();
    const accessToken = await this.getAccessToken(true);
    //Prevents the event to trigger if the event type is not selected
    if(e instanceof CustomEvent && e.type === "selected"){
        console.log("addConsultantToClient", e);
        const conditionConsultant = ((e as CustomEvent).detail.index == (this.consultants.length));
        const detail = (e as CustomEvent).detail;
        let consultantID = conditionConsultant? null:this.consultants[detail.index].consultantID;
        //If consultant id and selected client exists then it sets the new consultant-client connection
        if (consultantID && this.selectedClients[0]) {
            const clientID = this.selectedClients[0].clientID;
            const consultant = this.consultants.find((consultant) => consultant.consultantID == consultantID);
            //If client id and consultant exist then it search a client with the clientID
            if (clientID && consultant) {
                const client = this.clients.find((client: Client) => client.clientID === BigInt(clientID));
                
                //If client exists then it will be proved if a consultant-client connection exists then it updates the value from old value to new value
                if (client) {
                    if(this.consultantClients.find((consultantClient: ConsultantClient) => 
                        consultantClient.clientID == client.clientID) && 
                        this.database){
                        console.log("Update");
                        //Deletes old consultant-client connection
                        ConsultantClient.delete(this.database, accessToken, clientID).then(()=>{
                            const consClient = new ConsultantClient(consultant.consultantID, client.clientID);
                            //Sets new consultant-client connection
                            if(this.database)consClient.save(this.database, accessToken).then(()=>{
                                //Resets selection of each row
                                this.tempSelectedRows.forEach((row:any) =>{
                                    row.selected = false;
                                });
                                this.selectedClients = [];
                            });
                        }); 
                    //If there is no connection consultant-client for the current client, then the new connection will be build
                    } else {
                        console.log("Insert");
                        const consClient = new ConsultantClient(consultant.consultantID, client.clientID);
                        //Sets new consultant-client connection
                        consClient.save(this.database!, accessToken).then(()=>{
                            this.tempSelectedRows.forEach((row:any) =>{
                                row.selected = false;
                            });
                            this.selectedClients = [];
                        });
                    }
                }        
            }
          //If consultant id not exists (also default value "Berater") and the selected client exists, then it deletes the consultant-client connection
          } else if(this.selectedClients[0]){
            //Delete consultant-client connection
            ConsultantClient.delete(this.database!, accessToken, this.selectedClients[0].clientID).then(()=>{
                console.log("Verknüpfung gelöscht")
            });
            this.tempSelectedRows.forEach((row:any) =>{
                row.selected = false;
            });
            this.selectedClients = [];
        }
        this.requestUpdate();
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-clients": HitlClients;
  }
}